import { create } from 'zustand';
import { bc } from '@src/context/BroadcastChannelContext';

// Sentinel emitted by the backend "Authenticate User" function while the
// maintenance_mode env var is "true". Frozen cross-stack contract — must match
// the backend literal exactly (specs/002-maintenance-mode/contracts/maintenance-signal.md).
export const MAINTENANCE_SENTINEL = 'MAINTENANCE_MODE';

// Cross-tab signal type, on the same broadcast channel logout uses,
// so every open tab switches to the maintenance page together.
export const MAINTENANCE_BROADCAST_TYPE = 'maintenance';

// Reverse signal: a tab that booted successfully after the freeze ended tells
// tabs still sitting on the maintenance page to reload themselves.
export const MAINTENANCE_OVER_BROADCAST_TYPE = 'maintenance-over';

export const isMaintenanceError = (error) => error?.response?.status === 403
    && error?.response?.data?.message === MAINTENANCE_SENTINEL;

// Global soft-freeze flag. Set once when any request returns the maintenance
// sentinel (or another tab broadcasts it); cleared only by a full page reload,
// keeping the state one-directional.
export const useMaintenanceStore = create((set) => ({
    isMaintenanceMode: false,
    setMaintenanceMode: (value) => set({ isMaintenanceMode: value }),
}));

// Flag this tab and signal all other open tabs. The local set is required
// because BroadcastChannel does not echo messages back to the sender.
// postMessage is optional-chained: the test mock of the channel is a bare EventTarget.
export const activateMaintenanceMode = () => {
    useMaintenanceStore.getState().setMaintenanceMode(true);
    bc.postMessage?.({ type: MAINTENANCE_BROADCAST_TYPE });
};

// Broadcasts from other tabs arrive as CustomEvents re-dispatched on the
// channel by BroadcastChannelContext.
bc.addEventListener(MAINTENANCE_BROADCAST_TYPE, () => {
    useMaintenanceStore.getState().setMaintenanceMode(true);
});

// Auto-recovery: only tabs actually stuck on the maintenance page reload.
// A reloaded tab that still hits the freeze just lands back on the
// maintenance page — no reload loop.
bc.addEventListener(MAINTENANCE_OVER_BROADCAST_TYPE, () => {
    if (useMaintenanceStore.getState().isMaintenanceMode) {
        window.location.reload();
    }
});

export default useMaintenanceStore;
